import React, { useState, useEffect } from 'react';
import { database, ref, get } from '../../firebase';

const AdminFundHistory = () => {
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [sponsorFilter, setSponsorFilter] = useState('');
  const [walletFilter, setWalletFilter] = useState('');
  const [expandedTransaction, setExpandedTransaction] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [stats, setStats] = useState({
    totalAmount: 0,
    totalTransactions: 0,
    agWalletTotal: 0,
    agPowerTotal: 0,
    agPayTotal: 0,
    agWalletNet: 0,
    agPowerNet: 0,
    agPayNet: 0,
    memberUsedAmount: 0,
    memberUsedCount: 0,
    creditedCount: 0,
    debitedCount: 0,
    totalCredited: 0,
    totalDebited: 0
  });

  // Check if mobile view
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch all transactions from adminaddedhistory using Firebase SDK
  const fetchAllTransactions = async () => {
    setLoading(true);
    try {
      // Fetch admin history
      const historyRef = ref(database, 'adminaddedhistory');
      const historySnapshot = await get(historyRef);
      
      // Fetch users for member used amount
      const usersRef = ref(database, 'users');
      const usersSnapshot = await get(usersRef);
      
      const allTransactions = [];
      const dateList = [];
      let memberUsedAmount = 0;
      let memberUsedCount = 0;

      if (historySnapshot.exists()) {
        const datesData = historySnapshot.val();

        // Iterate through each date
        for (const dateKey in datesData) {
          dateList.push(dateKey);
          const dateData = datesData[dateKey];
          
          // Iterate through each sponsor under this date
          for (const sponsorId in dateData) {
            const sponsorTransactions = dateData[sponsorId];
            
            if (Array.isArray(sponsorTransactions)) {
              // Add date information to each transaction
              sponsorTransactions.forEach(transaction => {
                if (transaction && typeof transaction === 'object') {
                  allTransactions.push({
                    ...transaction,
                    sponsorId: transaction?.sponsorId || sponsorId,
                    historyDate: dateKey
                  });
                }
              });
            } else if (sponsorTransactions && typeof sponsorTransactions === 'object') {
              // If it's a single transaction object
              allTransactions.push({
                ...sponsorTransactions,
                sponsorId: sponsorTransactions?.sponsorId || sponsorId,
                historyDate: dateKey
              });
            }
          }
        }
      }

      // Member used amount from user-side ActivatePackage.js package entries
      if (usersSnapshot.exists()) {
        const usersData = usersSnapshot.val();
        Object.values(usersData).forEach((usersByDate) => {
          if (!usersByDate || typeof usersByDate !== 'object') return;
          Object.values(usersByDate).forEach((user) => {
            if (!user || typeof user !== 'object' || !Array.isArray(user.packages)) return;
            user.packages.forEach((pkg) => {
              if (!pkg || typeof pkg !== 'object') return;
              const walletUsed = String(pkg.walletUsed || '').toLowerCase();
              if (!walletUsed.includes('Aerovolt') && !walletUsed.includes('AV pay')) return;
              const usedAmount = Number(pkg.totalAmount || pkg.investmentAmount || 0);
              if (usedAmount > 0) {
                memberUsedAmount += usedAmount;
                memberUsedCount += 1;
              }
            });
          });
        });
      }

      // Sort by date (newest first)
      const sortedData = allTransactions.sort((a, b) => 
        new Date(b.date || 0) - new Date(a.date || 0)
      );
      
      setTransactions(sortedData);
      setFilteredTransactions(sortedData);
      setAvailableDates(dateList.sort().reverse());
      calculateStats(sortedData, { memberUsedAmount, memberUsedCount });
      
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on mount
  useEffect(() => {
    fetchAllTransactions();
  }, []);

  // Calculate stats
  const calculateStats = (data, extra = {}) => {
    // Gross total (absolute) so both credited + debited are counted in total
    const total = data.reduce((sum, t) => sum + Math.abs(Number(t.amount || 0)), 0);
    const agWalletRows = data.filter(t => t.walletType === 'AV Wallet');
    const agPowerRows = data.filter(t => t.walletType === 'AV Power Wallet');
    const agPayRows = data.filter(t => t.walletType === 'AV Pay Wallet');
    const agWalletTotal = agWalletRows.reduce((sum, t) => sum + Math.abs(Number(t.amount || 0)), 0);
    const agPowerTotal = agPowerRows.reduce((sum, t) => sum + Math.abs(Number(t.amount || 0)), 0);
    const agPayTotal = agPayRows.reduce((sum, t) => sum + Math.abs(Number(t.amount || 0)), 0);
    const agWalletNet = agWalletRows.reduce((sum, t) => sum + Number(t.amount || 0), 0);
    const agPowerNet = agPowerRows.reduce((sum, t) => sum + Number(t.amount || 0), 0);
    const agPayNet = agPayRows.reduce((sum, t) => sum + Number(t.amount || 0), 0);
    const credited = data.filter((t) => Number(t.amount || 0) >= 0);
    const debited = data.filter((t) => Number(t.amount || 0) < 0);
    const totalCredited = credited.reduce((sum, t) => sum + Number(t.amount || 0), 0);
    const totalDebited = debited.reduce((sum, t) => sum + Math.abs(Number(t.amount || 0)), 0);
    
    setStats({
      totalAmount: total,
      totalTransactions: data.length,
      agWalletTotal: agWalletTotal,
      agPowerTotal: agPowerTotal,
      agPayTotal: agPayTotal,
      agWalletNet,
      agPowerNet,
      agPayNet,
      memberUsedAmount: Number(extra.memberUsedAmount || stats.memberUsedAmount || 0),
      memberUsedCount: Number(extra.memberUsedCount || stats.memberUsedCount || 0),
      creditedCount: credited.length,
      debitedCount: debited.length,
      totalCredited,
      totalDebited
    });
  };

  // Apply filters
  useEffect(() => {
    let filtered = [...transactions];

    // Search filter (by name, userId, sponsorId, transactionId)
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(t => 
        (t.userName?.toLowerCase().includes(term)) ||
        (t.userId?.toLowerCase().includes(term)) ||
        (t.sponsorId?.toLowerCase().includes(term)) ||
        (t.transactionId?.toLowerCase().includes(term)) ||
        (t.description?.toLowerCase().includes(term))
      );
    }

    // Date filter (by transaction date)
    if (dateFilter) {
      const filterDate = new Date(dateFilter).toDateString();
      filtered = filtered.filter(t => {
        const txDate = new Date(t.date).toDateString();
        return txDate === filterDate;
      });
    }

    // History date filter
    if (selectedDate) {
      filtered = filtered.filter(t => t.historyDate === selectedDate);
    }

    // Sponsor ID filter
    if (sponsorFilter) {
      filtered = filtered.filter(t => 
        t.sponsorId?.toLowerCase().includes(sponsorFilter.toLowerCase())
      );
    }

    // Wallet type filter
    if (walletFilter) {
      filtered = filtered.filter(t => t.walletType === walletFilter);
    }

    setFilteredTransactions(filtered);
    calculateStats(filtered, {
      memberUsedAmount: stats.memberUsedAmount,
      memberUsedCount: stats.memberUsedCount
    });
  }, [searchTerm, dateFilter, selectedDate, sponsorFilter, walletFilter, transactions]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid date';
    }
  };

  // Format amount
  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const getCreditDebitStatus = (transaction) => {
    const amount = Number(transaction?.amount || 0);
    const txType = String(transaction?.transactionType || '').toLowerCase();
    if (amount < 0 || txType.includes('debit')) return 'Debited';
    return 'Credited';
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setDateFilter('');
    setSelectedDate('');
    setSponsorFilter('');
    setWalletFilter('');
  };

  // Toggle transaction expansion for mobile
  const toggleExpand = (transactionId) => {
    if (expandedTransaction === transactionId) {
      setExpandedTransaction(null);
    } else {
      setExpandedTransaction(transactionId);
    }
  };

  // Mobile Transaction Card
  const MobileTransactionCard = ({ transaction }) => {
    const isExpanded = expandedTransaction === transaction.transactionId;
    
    return (
      <div 
        className="bg-[#1A1F2E] rounded-lg border border-[#2A2F3E] mb-2 overflow-hidden"
        onClick={() => toggleExpand(transaction.transactionId)}
      >
        <div className="p-3">
          {/* Header with basic info */}
          <div className="flex justify-between items-start mb-2">
            <div>
              <div className="text-white text-sm font-medium">{transaction.userName || 'Unknown'}</div>
              <div className="text-gray-400 text-xs">ID: {transaction.userId || 'N/A'}</div>
            </div>
            <div className={`px-2 py-1 rounded text-xs font-medium ${
              transaction.walletType === 'AV Wallet' 
                ? 'bg-yellow-900/30 text-yellow-400' 
                : transaction.walletType === 'AV Pay Wallet'
                ? 'bg-emerald-900/30 text-emerald-400'
                : 'bg-blue-900/30 text-blue-400'
            }`}>
              {transaction.walletType || 'N/A'}
            </div>
          </div>

          {/* Amount and date */}
          <div className="flex justify-between items-center mb-2">
            <span className={`${Number(transaction.amount || 0) < 0 ? 'text-red-400' : 'text-green-400'} font-bold text-base`}>
              {formatAmount(transaction.amount)}
            </span>
            <span className="text-gray-400 text-xs">{formatDate(transaction.date)}</span>
          </div>

          <div className="mb-1">
            <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-medium ${
              getCreditDebitStatus(transaction) === 'Debited'
                ? 'bg-red-900/30 text-red-400'
                : 'bg-green-900/30 text-green-400'
            }`}>
              {getCreditDebitStatus(transaction)}
            </span>
          </div>

          {/* Sponsor ID */}
          <div className="text-gray-400 text-xs mb-1">
            Sponsor: <span className="text-blue-400">{transaction.sponsorId || 'N/A'}</span>
          </div>

          {/* History Date Badge */}
          {transaction.historyDate && (
            <div className="text-gray-500 text-xs mb-1">
              History Date: {transaction.historyDate}
            </div>
          )}

          {/* Expand indicator */}
          <div className="flex justify-center mt-1">
            <svg 
              className={`w-4 h-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>

          {/* Expanded details */}
          {isExpanded && (
            <div className="mt-3 pt-3 border-t border-[#2A2F3E] space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="text-gray-400 text-xs">Transaction ID</div>
                  <div className="text-white text-xs font-mono break-all">{transaction.transactionId || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-gray-400 text-xs">Type</div>
                  <div className="text-white text-xs">{transaction.transactionType || 'N/A'}</div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="text-gray-400 text-xs">Previous Balance</div>
                  <div className="text-white text-xs">{formatAmount(transaction.previousBalance)}</div>
                </div>
                <div>
                  <div className="text-gray-400 text-xs">New Balance</div>
                  <div className="text-white text-xs">{formatAmount(transaction.newBalance)}</div>
                </div>
              </div>

              {transaction.description && (
                <div>
                  <div className="text-gray-400 text-xs">Description</div>
                  <div className="text-white text-xs bg-[#0B0E14] p-2 rounded">
                    {transaction.description}
                  </div>
                </div>
              )}

              <div>
                <div className="text-gray-400 text-xs">Admin</div>
                <div className="text-white text-xs">{transaction.adminName || 'Admin'}</div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#0B0E14] font-body p-3">
      {/* Header */}
      <div className="bg-[#1A1F2E] rounded-xl p-4 mb-3 border border-[#2A2F3E]">
        <h1 className="text-lg font-bold text-white">Admin Fund History</h1>
        <p className="text-gray-400 text-xs mt-1">View all admin-added fund transactions from adminaddedhistory</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-7 gap-2 mb-3">
        <div className="bg-[#1A1F2E] p-3 rounded-lg border border-[#2A2F3E]">
          <div className="text-gray-400 text-xs">Total Amount (Gross)</div>
          <div className="text-white text-lg font-bold">{formatAmount(stats.totalAmount)}</div>
          <div className="text-gray-500 text-[10px]">
            C: {formatAmount(stats.totalCredited)} | D: {formatAmount(stats.totalDebited)}
          </div>
        </div>
        <div className="bg-[#1A1F2E] p-3 rounded-lg border border-[#2A2F3E]">
          <div className="text-gray-400 text-xs">Transactions</div>
          <div className="text-white text-lg font-bold">{stats.totalTransactions}</div>
          <div className="text-gray-500 text-[10px]">
            Credited: {stats.creditedCount} | Debited: {stats.debitedCount}
          </div>
        </div>
        <div className="bg-[#1A1F2E] p-3 rounded-lg border border-[#2A2F3E]">
          <div className="text-gray-400 text-xs">AV Wallet (Net)</div>
          <div className="text-yellow-400 text-lg font-bold">{formatAmount(stats.agWalletNet)}</div>
          <div className="text-gray-500 text-[10px]">
            C: {formatAmount(
              filteredTransactions
                .filter((t) => t.walletType === 'AV Wallet' && Number(t.amount || 0) >= 0)
                .reduce((s, t) => s + Number(t.amount || 0), 0)
            )} | D: {formatAmount(
              filteredTransactions
                .filter((t) => t.walletType === 'AV Wallet' && Number(t.amount || 0) < 0)
                .reduce((s, t) => s + Math.abs(Number(t.amount || 0)), 0)
            )}
          </div>
        </div>
        <div className="bg-[#1A1F2E] p-3 rounded-lg border border-[#2A2F3E]">
          <div className="text-gray-400 text-xs">AV Power (Net)</div>
          <div className="text-blue-400 text-lg font-bold">{formatAmount(stats.agPowerNet)}</div>
          <div className="text-gray-500 text-[10px]">
            C: {formatAmount(
              filteredTransactions
                .filter((t) => t.walletType === 'AV Power Wallet' && Number(t.amount || 0) >= 0)
                .reduce((s, t) => s + Number(t.amount || 0), 0)
            )} | D: {formatAmount(
              filteredTransactions
                .filter((t) => t.walletType === 'AV Power Wallet' && Number(t.amount || 0) < 0)
                .reduce((s, t) => s + Math.abs(Number(t.amount || 0)), 0)
            )}
          </div>
        </div>
        <div className="bg-[#1A1F2E] p-3 rounded-lg border border-[#2A2F3E]">
          <div className="text-gray-400 text-xs">AV Pay Wallet (Net)</div>
          <div className="text-emerald-400 text-lg font-bold">{formatAmount(stats.agPayNet)}</div>
          <div className="text-gray-500 text-[10px]">
            C: {formatAmount(
              filteredTransactions
                .filter((t) => t.walletType === 'AV Pay Wallet' && Number(t.amount || 0) >= 0)
                .reduce((s, t) => s + Number(t.amount || 0), 0)
            )} | D: {formatAmount(
              filteredTransactions
                .filter((t) => t.walletType === 'AV Pay Wallet' && Number(t.amount || 0) < 0)
                .reduce((s, t) => s + Math.abs(Number(t.amount || 0)), 0)
            )}
          </div>
        </div>
        <div className="bg-[#1A1F2E] p-3 rounded-lg border border-[#2A2F3E]">
          <div className="text-gray-400 text-xs">Member Used Amount</div>
          <div className="text-orange-400 text-lg font-bold">{formatAmount(stats.memberUsedAmount)}</div>
          <div className="text-gray-500 text-[10px]">
            ActivatePackage uses: {stats.memberUsedCount}
          </div>
        </div>
        <div className="bg-[#1A1F2E] p-3 rounded-lg border border-[#2A2F3E]">
          <div className="text-gray-400 text-xs">Balance After Use</div>
          <div className="text-emerald-400 text-lg font-bold">
            {formatAmount((stats.agWalletNet + stats.agPowerNet + stats.agPayNet) - stats.memberUsedAmount)}
          </div>
          <div className="text-gray-500 text-[10px]">
            (AV Wallet Net + AV Power Net + AV Pay Wallet Net) - Member Used
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-[#1A1F2E] rounded-xl p-4 mb-3 border border-[#2A2F3E]">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <h2 className="text-white font-medium text-sm">Filters</h2>
          {(searchTerm || dateFilter || selectedDate || sponsorFilter || walletFilter) && (
            <button
              onClick={clearFilters}
              className="text-xs text-red-400 hover:text-red-300 ml-auto"
            >
              Clear All
            </button>
          )}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
          {/* Search */}
          <div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, ID, transaction..."
              className="w-full px-3 py-2 bg-[#0B0E14] border border-[#2A2F3E] rounded-lg text-white placeholder:text-gray-500 text-xs focus:outline-none focus:border-[#2A6BFF]"
            />
          </div>

          {/* Transaction Date Filter */}
          <div>
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="w-full px-3 py-2 bg-[#0B0E14] border border-[#2A2F3E] rounded-lg text-white text-xs focus:outline-none focus:border-[#2A6BFF]"
            />
          </div>

          {/* History Date Filter */}
          <div>
            <select
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-full px-3 py-2 bg-[#0B0E14] border border-[#2A2F3E] rounded-lg text-white text-xs focus:outline-none focus:border-[#2A6BFF]"
            >
              <option value="">All History Dates</option>
              {availableDates.map(date => (
                <option key={date} value={date}>{date}</option>
              ))}
            </select>
          </div>

          {/* Sponsor Filter */}
          <div>
            <input
              type="text"
              value={sponsorFilter}
              onChange={(e) => setSponsorFilter(e.target.value)}
              placeholder="Filter by Sponsor ID"
              className="w-full px-3 py-2 bg-[#0B0E14] border border-[#2A2F3E] rounded-lg text-white placeholder:text-gray-500 text-xs focus:outline-none focus:border-[#2A6BFF]"
            />
          </div>

          {/* Wallet Type Filter */}
          <div>
            <select
              value={walletFilter}
              onChange={(e) => setWalletFilter(e.target.value)}
              className="w-full px-3 py-2 bg-[#0B0E14] border border-[#2A2F3E] rounded-lg text-white text-xs focus:outline-none focus:border-[#2A6BFF]"
            >
              <option value="">All Wallets</option>
              <option value="AV Wallet">AV Wallet</option>
              <option value="AV Power Wallet">AV Power Wallet</option>
              <option value="AV Pay Wallet">AV Pay Wallet</option>
            </select>
          </div>
        </div>
      </div>

      {/* Transactions List */}
      <div className="bg-[#1A1F2E] rounded-xl p-4 border border-[#2A2F3E]">
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-white font-medium text-sm">
            Transaction History ({filteredTransactions.length})
          </h2>
          <button
            onClick={fetchAllTransactions}
            className="text-xs text-blue-400 hover:text-blue-300"
          >
            Refresh
          </button>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#2A6BFF] mx-auto"></div>
            <p className="text-gray-400 text-sm mt-2">Loading transactions...</p>
          </div>
        ) : filteredTransactions.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-400 text-sm">No transactions found in adminaddedhistory</p>
          </div>
        ) : isMobile ? (
          /* Mobile View - Cards */
          <div className="space-y-2">
            {filteredTransactions.map((transaction) => (
              <MobileTransactionCard 
                key={transaction.transactionId || Math.random()} 
                transaction={transaction} 
              />
            ))}
          </div>
        ) : (
          /* Desktop View - Table */
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#2A2F3E]">
                  <th className="text-left py-2 px-2 text-gray-400 text-xs font-medium">Date</th>
                  <th className="text-left py-2 px-2 text-gray-400 text-xs font-medium">History Date</th>
                  <th className="text-left py-2 px-2 text-gray-400 text-xs font-medium">Member</th>
                  <th className="text-left py-2 px-2 text-gray-400 text-xs font-medium">User ID</th>
                  <th className="text-left py-2 px-2 text-gray-400 text-xs font-medium">Sponsor ID</th>
                  <th className="text-left py-2 px-2 text-gray-400 text-xs font-medium">Wallet</th>
                  <th className="text-left py-2 px-2 text-gray-400 text-xs font-medium">Status</th>
                  <th className="text-right py-2 px-2 text-gray-400 text-xs font-medium">Amount</th>
                  <th className="text-right py-2 px-2 text-gray-400 text-xs font-medium">New Balance</th>
                  <th className="text-left py-2 px-2 text-gray-400 text-xs font-medium">Transaction ID</th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.map((transaction) => (
                  <tr key={transaction.transactionId || Math.random()} className="border-b border-[#2A2F3E] hover:bg-[#0B0E14]/50">
                    <td className="py-2 px-2 text-white text-xs">{formatDate(transaction.date)}</td>
                    <td className="py-2 px-2 text-gray-400 text-xs">{transaction.historyDate || 'N/A'}</td>
                    <td className="py-2 px-2 text-white text-xs font-medium">{transaction.userName || 'Unknown'}</td>
                    <td className="py-2 px-2 text-white text-xs font-mono">{transaction.userId || 'N/A'}</td>
                    <td className="py-2 px-2 text-blue-400 text-xs font-mono">{transaction.sponsorId || 'N/A'}</td>
                    <td className="py-2 px-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        transaction.walletType === 'AV Wallet' 
                          ? 'bg-yellow-900/30 text-yellow-400' 
                          : transaction.walletType === 'AV Pay Wallet'
                          ? 'bg-emerald-900/30 text-emerald-400'
                          : 'bg-blue-900/30 text-blue-400'
                      }`}>
                        {transaction.walletType || 'N/A'}
                      </span>
                    </td>
                    <td className="py-2 px-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        getCreditDebitStatus(transaction) === 'Debited'
                          ? 'bg-red-900/30 text-red-400'
                          : 'bg-green-900/30 text-green-400'
                      }`}>
                        {getCreditDebitStatus(transaction)}
                      </span>
                    </td>
                    <td className={`py-2 px-2 text-xs font-bold text-right ${Number(transaction.amount || 0) < 0 ? 'text-red-400' : 'text-green-400'}`}>
                      {formatAmount(transaction.amount)}
                    </td>
                    <td className="py-2 px-2 text-yellow-400 text-xs font-bold text-right">
                      {formatAmount(transaction.newBalance)}
                    </td>
                    <td className="py-2 px-2 text-gray-400 text-xs font-mono" title={transaction.transactionId}>
                      {transaction.transactionId || 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminFundHistory;